import { addHours } from "date-fns";
import subByTime from "date-fns/sub";
import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";
import { useSearchParams } from "react-router-dom";
import MultipleSelection from "src/components/MultipleSelection,";

import { OperationTransactionStatus } from "@wallet-manager/node-types/dist/src/postgres/const";

import { getOperationTransaction, listFace } from "../../api/merchant";
import { OperationsTransactionSearchParamsFace } from "../../api/types";
import { AssetMultiSelection } from "../../components/AssetSelection";
import { ChainSingleSelection } from "../../components/ChainSelection";
import { useDatePicker } from "../../components/DatePicker";
import { useAssets, useChains } from "../../components/FetchConfig";
import GeneralBtn from "../../components/GeneralBtn";
import MpTextField from "../../components/MpTextField";
import {
  Box,
  Container,
  SelectChangeEvent,
} from "../../components/MuiGenerals";
import { useAlerting, usePermission, useTabs } from "../../hooks";
import {
  enumOperationType,
  FeatureCodes,
  tableConfig,
} from "../../utils/constant";
import {
  downloadFiles,
  findChainInfo,
  findEnumKey,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../utils/helper";
import { DivideLine, genField, useRefresh } from "../../utils/HelperComp";
import { customSx } from "../../utils/styling";
import { useAppSelector } from "../../reducer/hooks";
import { selectProfile } from "../../reducer/profileSlice";
import { useZusDialogStore } from "../../zustand/store";
import OperationTransactionRecordList from "./OperationTransactionRecordsList";

const Pkey = FeatureCodes.assetManagement.OperationTransactionRecords;
export default function OperationTransactionsRecords() {
  const translate = useTranslate();
  const t = (key: string) => translate(`operations.${key}`);
  const [page, setPage] = useState(0);
  const [list, setList] = useState<listFace>({ rows: [], count: 0 });
  const tabsArr = [{ name: t("table"), value: "" }];
  const { Tabs } = useTabs(tabsArr);
  const [refreshBool, refresh] = useRefresh();

  return (
    <Box sx={customSx.layoutBox}>
      <Container disableGutters maxWidth={false}>
        <FilterBar
          setList={setList}
          setPage={setPage}
          page={page}
          count={list.count}
          refreshBool={refreshBool}
          listMapping={listMapping}
        />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Tabs>
          <OperationTransactionRecordList
            list={list}
            page={page}
            setPage={setPage}
            refresh={refresh}
            listMapping={listMapping}
          />
        </Tabs>
      </Container>
    </Box>
  );
}

const rawSearchParams = {
  batchId: [],
  chain_name: "",
  assetName: [],
  statuses: [],
};

function FilterBar(props: any) {
  const translate = useTranslate();
  const t = (key: string) => translate(`operations.${key}`);
  const tc = (key: string) => translate(`common.${key}`);
  const { alerting } = useAlerting();
  const { hasPermission } = usePermission();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const profile = useAppSelector(selectProfile);
  const { setList, setPage, page, count, listMapping, refreshBool } = props;
  const [searchedParams] = useSearchParams();
  const initSearchParams = {
    ...rawSearchParams,
    batchId: searchedParams.get("batchId") || "",
  };
  const [fields, setFields] =
    useState<OperationsTransactionSearchParamsFace>(initSearchParams);

  function getNow() {
    const timezone = profile.timezone;
    const defaultTimezone = (new Date().getTimezoneOffset() * -1) / 60;
    const totalDiff = Number(timezone) - defaultTimezone;
    return addHours(new Date(), totalDiff);
  }
  const now = getNow();
  const oneMonthAgo = new Date(
    subByTime(now, { months: 1 }).toDateString() + ", 00:00:00",
  );
  const endOfToday = new Date(now.toDateString() + ", 23:59:59");
  const defaultCreatedDates = {
    start: { defaultDate: oneMonthAgo },
    end: { defaultDate: endOfToday },
  };

  const DateObj = {
    Created: useDatePicker(defaultCreatedDates),
    Modified: useDatePicker(),
  };
  const allAssetNames = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });
  const setAssets = (assetName: string[]) =>
    setFields((fields) => ({ ...fields, assetName }));
  const getParams = () => {
    const { chain_name, batchId, ...rest } = fields;
    const { chain_id, chain_type } = selectChainByPieces({ chain_name });
    return {
      ...rest,
      created_date_from: DateObj.Created.start,
      created_date_to: DateObj.Created.end,
      last_modified_date_from: DateObj.Modified.start,
      last_modified_date_to: DateObj.Modified.end,
      batch_id: batchId ? [batchId] : [],
      chain_id,
      chain_type,
      page,
      pageSize: tableConfig.pageSize,
    };
  };

  const params = getParams();
  const [cacheParams, setCacheParams] = useState(params);
  const zusDialog = useZusDialogStore();
  useEffect(() => {
    const fetch = async () => {
      zusDialog.openExtra("loadingDialog");
      const res = await getOperationTransaction({ ...cacheParams, page });
      zusDialog.closeExtra();
      if (!res) return;
      setList(res);
    };
    fetch();
  }, [page, cacheParams, refreshBool]);

  useEffect(() => {
    setAssets([]);
  }, [fields.chain_name]);

  const onSearch = async () => {
    setPage(0);
    setCacheParams(params);
  };
  const onReset = () => {
    setFields(initSearchParams);
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };

  const onArraySelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    const newValue = typeof value === "string" ? value.split(",") : value;
    return setFields((fields) => ({ ...fields, [name]: newValue }));
  };

  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getOperationTransaction({ ...params, page, pageSize }, { signal });
    const rawRes = await getFullApiResponse(apiFn, count);
    if (rawRes.length === 0) return;
    const res = listMapping("name", rawRes, translate);
    const config = {};
    downloadFiles(`Operation Transaction Records`, res, config);
  };
  const onChange =
    (type: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const F = genField({ t }, [
    ["created_date", <DateObj.Created.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.Modified.Picker type="dateTime" />],
    [
      "batch_id",
      <MpTextField
        fullWidth={true}
        name="batchId"
        label={t("phBatch_id")}
        value={fields.batchId}
        onChange={onChange("batchId")}
      />,
    ],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("phChain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
      />,
    ],
    [
      "asset_name",
      <AssetMultiSelection
        label={t("phAsset_name")}
        setChoices={setAssets}
        choices={fields.assetName}
        allItems={allAssetNames}
      />,
    ],
    [
      "status",
      <MultipleSelection
        label={t("phStatus")}
        value={fields.statuses}
        onChange={(statuses) => setFields((f) => ({ ...f, statuses }))}
        enumData={OperationTransactionStatus}
      />,
    ],
  ]);

  return (
    <>
      <Box sx={customSx.filterCmdBar}>
        <GeneralBtn
          label="export"
          isHidden={!hasPermission(Pkey.Export)}
          onClick={onExport}
        />
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.created_date}
        {F.last_modified_time}
        {F.chain_name}
        {F.asset_name}
        {F.status}
        {F.batch_id}
        {F.operation_type}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  translate: any,
): any[][] => {
  const t = (key: string) => translate(`operations.${key}`);
  const omitKeys = which === "name" ? ["rawData"] : [];
  const res = array.map((item: any) => {
    // const parseJson = (input: any) =>
    // input ? JSON.stringify(JSON.parse(input), null, 3) : "";
    const mappedResult = [
      ["batch_id", t("batch_id"), item.batch_id],
      [
        "chain_name",
        t("chain_name"),
        findChainInfo(String(item?.batch?.chain_type), item?.batch?.chain_id)
          ?.name,
      ],
      ["step_seq", t("step_seq"), item.step_seq],
      ["tx_seq", t("tx_seq"), item.tx_seq],
      [
        "status",
        t("status"),
        translate(`enumConstants.${OperationTransactionStatus[item.status]}`),
      ],
      [
        "tx_status",
        t("tx_status"),
        Number(item.status) === 1 || Number(item.status) === 2
          ? ""
          : translate(`enumConstants.${String(item.tx_status)}`),
      ],
      [
        "operation_type",
        t("operation_type"),
        translate(
          `enumOperationType.${findEnumKey(
            enumOperationType,
            item.operation_type,
          )}`,
        ),
      ],
      ["asset_name", t("asset_name"), item.asset_name],
      ["tx_hash", t("tx_hash"), item.tx_hash],
      ["user_operation_hash", t("user_operation_hash"), item.user_op_hash],
      ["error_reason", t("error_reason"), item.error_reason],
      ["remark", t("remark"), item.remarks],
      [
        "created_date",
        t("created_date"),
        toDisplayTime(item?.batch?.created_date),
      ],
      [
        "last_modified_date",
        t("last_modified_date"),
        toDisplayTime(item?.batch?.last_modified_date),
      ],
      ["rawData", "", item],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });
  const output = res.map(listMappingTransform(which));
  return output;
};
